import React, {useMemo, useEffect, useState, useLayoutEffect} from 'react';
import {
    Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent,
    DialogTitle, Divider, IconButton, Typography, Stack
} from '@mui/material';
import {AutocompleteField, InputField, DatePicker, MoneyField, OverlayMask} from '@/components/common';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import {LoanScheduleResponse, LoanScheduleRequest, LoanScheduleItemVO} from '@/services/auditService/auditLoanCalculatorService';
import RepaymentScheduleDrawer from '@/pages/components/audit/adjustment/adjust/dialogs/loan/RepaymentScheduleDrawer';
import type {BankLoanScheduleSummaryTrace} from '@/pages/components/audit/adjustment/adjust/dialogs/loan/RepaymentScheduleDrawer';
import {VehicleLoanRow} from '@/services/auditService/interface/wp-row';
import {handleFormValidate} from '@/utils/formValidateUtil';
import {
    addMonths,
    parseDDMMYYYY,
    formatYYYYMMDD,
    formatDDMMYYYY,
    toNumber,
    formatNumber,
    inRange,
} from '@/utils/dataUtils';
import {
    resolveVehicleLoanYearEndBalanceLast,
    vehicleLoanSumsHasTrackableValues,
} from '@/pages/components/audit/adjustment/adjust/dialogs/loan/vehicleLoanSumUtils';
import LoanSummaryCard, {type LoanSummaryCell} from '@/pages/components/audit/adjustment/adjust/dialogs/loan/LoanSummaryCard';
import {
    emptyVehicleLoanSums,
    normalizeLegacyLoanSums,
    resolveBalanceYoa,
    resolveNonCurrentLastYoa,
} from '@/pages/components/audit/adjustment/adjust/dialogs/loan/loanSummarySums';

/** Preset term lengths (months) for Terms Autocomplete. */
const VEHICLE_LOAN_TERM_OPTIONS = ['12', '24', '36', '48', '60'];

/** Computed monthly instalment: ceil(totalInstalment / terms). */
function calcMonthlyInstalment(totalInstalment: number | string, terms: number | null | undefined): number {
    if (!terms || terms <= 0) return 0;
    return Math.ceil(toNumber(totalInstalment) / terms);
}

/** Persist monthly: user suffix if ≠ system calc, else system calc (2-decimal compare). */
function resolveMonthlyInstalmentForSave(systemCalc: number, userInput: number): number {
    return toNumber(userInput) !== toNumber(systemCalc) ? toNumber(userInput) : systemCalc;
}

/** True when every inCurr Instalment equals Monthly instalment (empty inCurr ⇒ treat as match). */
function inCurrInstalmentsMatchMonthly(
    schedule: LoanScheduleItemVO[],
    periodFrom: string | null | undefined,
    periodTo: string | null | undefined,
    monthly: number,
): boolean {
    const rows = schedule.filter((r) =>
        inRange(r.repaymentDate, periodFrom ?? undefined, periodTo ?? undefined),
    );
    if (rows.length === 0) return true;
    const m = toNumber(monthly);
    return rows.every((r) => toNumber(r.instalmentAmount) === m);
}

export interface VehicleLoanProps {
    yoa: string;
    lastYoa: string;
    currencySymbol?: string;
    triggerVariant?: 'link' | 'icon';
    annualId?: number;
    initialData?: Partial<VehicleLoanRow> | null;
    onDataChange?: (data: Partial<VehicleLoanRow>) => void;
    onConfirm?: () => void;
    // Notify parent when the repayment drawer opens/closes (for layout animation)
    onDrawerStateChange?: (open: boolean) => void;
    readOnly?: boolean;
    // MV and purchase cost passed from parent
    parentMV?: string;
    parentPurchaseCost?: number;
    // 1.3: Hide lastYoa column when firstYearAudit=1
    hideLastYoa?: boolean;
    // Period end date for checking if loan is in future
    periodTo?: string | null;
}

interface VehicleLoanForm {
    mv: string;
    hpComp: string;
    agreementNo: string;
    purchaseCost: string;
    downPayment: string;
    terms: number | null;
    totalInstalment: string;
    firstRepayment: string;
}

const initialForm: VehicleLoanForm = {
    mv: '',
    hpComp: '',
    agreementNo: '',
    purchaseCost: '',
    downPayment: '',
    terms: null,
    totalInstalment: '',
    firstRepayment: '',
};

const vehicleLoanRequiredFields = ['purchaseCost', 'downPayment', 'terms', 'totalInstalment', 'firstRepayment'];

export default function VehicleLoanDetail({
                                        yoa, lastYoa,
                                        currencySymbol = 'HKD',
                                        triggerVariant = 'link',
                                        annualId,
                                        initialData,
                                        onDataChange,
                                        onConfirm,
                                        onDrawerStateChange,
                                        readOnly = false,
                                        parentMV,
                                        parentPurchaseCost,
                                        hideLastYoa = false,
                                        periodTo = null
                                    }: VehicleLoanProps) {
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const cardRef = React.useRef<HTMLDivElement | null>(null);
    const isMountedRef = React.useRef(false);
    const onDataChangeRef = React.useRef(onDataChange);
    const isFirstRender = React.useRef(true);
    const initialSumsHadValuesRef = React.useRef(vehicleLoanSumsHasTrackableValues(initialData?.sums));
    // When sums are synced from parent, suppress back-propagation to parent to avoid ping-pong updates
    const syncingSumsFromParentRef = React.useRef(false);
    const [calcOpen, setCalcOpen] = useState(false);
    const [form, setForm] = useState<VehicleLoanForm>(() => {
        // Use parent values if provided, otherwise use initialData
        const mv = parentMV || initialData?.mv || '';
        const purchaseCost = parentPurchaseCost !== undefined ? 
            formatNumber(parentPurchaseCost) : 
            (initialData?.purchaseCost ? formatNumber(initialData.purchaseCost) : '');
        
        if (!initialData) {
            return {
                ...initialForm,
                mv,
                purchaseCost
            };
        }
        return {
            mv,
            hpComp: initialData.hpComp || '',
            agreementNo: initialData.agreementNo || '',
            purchaseCost,
            downPayment: formatNumber(initialData.downPayment || 0),
            terms: initialData.terms || null,
            totalInstalment: formatNumber(initialData.totalInstalment || 0),
            firstRepayment: initialData.firstRepayment || '',
        };
    });
    const [formError, setFormError] = useState<Record<string, string>>({});
    // Right side of Monthly instalment split; seed from saved override or system calc
    const [monthlyInstalmentInput, setMonthlyInstalmentInput] = useState<number>(() => {
        const terms = initialData?.terms || null;
        const total = initialData?.totalInstalment ?? 0;
        const calc = calcMonthlyInstalment(total, terms);
        const saved = initialData?.monthlyInstalment;
        if (saved != null && toNumber(saved) !== toNumber(calc)) {
            return toNumber(saved);
        }
        return calc;
    });
    const prevMonthlyCalcRef = React.useRef<number>(
        calcMonthlyInstalment(initialData?.totalInstalment ?? 0, initialData?.terms || null)
    );
    // Idle until Drawer init compares inCurr Instalment vs Monthly; then idle if equal, split if not.
    const [monthlyInstalmentSplitActive, setMonthlyInstalmentSplitActive] = useState(false);
    const monthlyModeDecidedRef = React.useRef(false);
    const monthlyInstalmentRef = React.useRef(0);
    const monthlyInstalmentInputRef = React.useRef(0);
    const [drawerOpen, setDrawerOpen] = useState(false);
    /** Summary calc hover → RepaymentScheduleDrawer period/column outline. */
    const [summaryTrace, setSummaryTrace] = useState<BankLoanScheduleSummaryTrace | null>(null);
    const [scheduleData, setScheduleData] = useState<LoanScheduleResponse | null>(null);
    const [shiftX, setShiftX] = useState<number>(0);
    // Width of the RepaymentScheduleDrawer Paper (0 when closed)
    const [drawerWidth, setDrawerWidth] = useState<number>(0);
    const [scheduleRequest, setScheduleRequest] = useState<LoanScheduleRequest | null>(null);
    const [sums, setSums] = useState<VehicleLoanRow['sums']>(
        normalizeLegacyLoanSums(initialData?.sums || emptyVehicleLoanSums()),
    );

    // Ref to track current sums for deep comparison (avoids infinite loops from bidirectional sync)
    const currentSumsRef = React.useRef(sums);
    currentSumsRef.current = sums;

    // Sync sums from parent when initialData.sums changes
    // (e.g. from parent's RepaymentScheduleDrawer recalculation or backend data reload)
    useEffect(() => {
        const parentSums = initialData?.sums;
        if (!parentSums) return;
        
        const cur = currentSumsRef.current;
        // Deep compare to avoid infinite loops from bidirectional sync
        const hasChanged =
            parentSums.currentYoa !== cur.currentYoa ||
            parentSums.nonCurrentYoa !== cur.nonCurrentYoa ||
            parentSums.balanceYoa !== cur.balanceYoa ||
            parentSums.interestYoa !== cur.interestYoa ||
            parentSums.currentLastYoa !== cur.currentLastYoa ||
            parentSums.nonCurrentLastYoa !== cur.nonCurrentLastYoa ||
            parentSums.balanceLastYoa !== cur.balanceLastYoa ||
            parentSums.interestLastYoa !== cur.interestLastYoa ||
            parentSums.currentYoaChange !== cur.currentYoaChange ||
            parentSums.currentLastYoaChange !== cur.currentLastYoaChange ||
            parentSums.nonCurrentYoaChange !== cur.nonCurrentYoaChange ||
            parentSums.interestYoaChange !== cur.interestYoaChange ||
            parentSums.balanceLastYoaChange !== cur.balanceLastYoaChange ||
            parentSums.interestLastYoaChange !== cur.interestLastYoaChange ||
            parentSums.prevFinalBalance !== cur.prevFinalBalance ||
            parentSums.currFinalBalance !== cur.currFinalBalance ||
            parentSums.editedInstalments !== cur.editedInstalments;
        
        if (hasChanged) {
            syncingSumsFromParentRef.current = true;
            initialSumsHadValuesRef.current = true;
            setSums(normalizeLegacyLoanSums(parentSums));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialData?.sums]);
    // Handle form changes
    const handleChange = (field: keyof VehicleLoanForm, value: any) => {
        setForm((updated) => {
            const next = {...updated, [field]: value};
            return next;
        });
        handleFormValidate(field, value, vehicleLoanRequiredFields, setFormError);
    };

    // Computed values
    const hpAmt = useMemo(() => {
        const pc = toNumber(form.purchaseCost);
        const dp = toNumber(form.downPayment);
        return Math.max(pc - dp, 0);
    }, [form.purchaseCost, form.downPayment]);

    const lastRepaymentDate = useMemo(() => {
        if (!form.firstRepayment || !form.terms || form.terms <= 0) return '';
        const base = parseDDMMYYYY(form.firstRepayment);
        if (!base) return '';
        const d = addMonths(base, Math.max(form.terms - 1, 0));
        return formatDDMMYYYY(d);
    }, [form.firstRepayment, form.terms]);

    const monthlyInstalment = useMemo(
        () => calcMonthlyInstalment(form.totalInstalment, form.terms),
        [form.totalInstalment, form.terms]
    );
    monthlyInstalmentRef.current = monthlyInstalment;

    // Keep right-side input in sync with system calc when user had not overridden
    useEffect(() => {
        const prev = prevMonthlyCalcRef.current;
        prevMonthlyCalcRef.current = monthlyInstalment;
        setMonthlyInstalmentInput((cur) => (
            toNumber(cur) === toNumber(prev) ? monthlyInstalment : cur
        ));
    }, [monthlyInstalment]);

    monthlyInstalmentInputRef.current = monthlyInstalmentInput;

    const totalInterest = useMemo(() => {
        const ti = toNumber(form.totalInstalment);
        // Total interest = Total Instalment - HP amt
        return Math.max(ti - hpAmt, 0);
    }, [hpAmt, form.totalInstalment]);

    useEffect(() => {
        isMountedRef.current = true;
        const timer = setTimeout(() => {
            isFirstRender.current = false;
        }, 0);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        onDataChangeRef.current = onDataChange;
    }, [onDataChange]);

    // Notify parent after commit (never during render / setState updaters)
    useEffect(() => {
        if (!isMountedRef.current || isFirstRender.current) return;
        if (syncingSumsFromParentRef.current) {
            syncingSumsFromParentRef.current = false;
            return;
        }
        if (onDataChangeRef.current) {
            const partialData: Partial<VehicleLoanRow> = {
                mv: form.mv,
                hpComp: form.hpComp,
                agreementNo: form.agreementNo,
                purchaseCost: toNumber(form.purchaseCost),
                downPayment: toNumber(form.downPayment),
                hpAmt: hpAmt,
                terms: form.terms || 0,
                totalInstalment: toNumber(form.totalInstalment),
                totalInterest: totalInterest,
                firstRepayment: form.firstRepayment,
                lastRepayment: lastRepaymentDate,
                monthlyInstalment: resolveMonthlyInstalmentForSave(monthlyInstalment, monthlyInstalmentInput),
            };
            // Avoid overwriting parent/backend sums with default zeros before calculator runs
            if (vehicleLoanSumsHasTrackableValues(sums) || initialSumsHadValuesRef.current) {
                partialData.sums = sums;
                if (vehicleLoanSumsHasTrackableValues(sums)) {
                    initialSumsHadValuesRef.current = true;
                }
            }
            onDataChangeRef.current(partialData);
        }
    }, [form.mv, form.hpComp, form.agreementNo, form.purchaseCost, form.downPayment, form.terms, form.totalInstalment, form.firstRepayment, hpAmt, totalInterest, lastRepaymentDate, monthlyInstalment, monthlyInstalmentInput, sums]);

    // Build schedule request when inputs change.
    // User Monthly override → monthlyInstalment on calcVehicleLoanSchedule (full schedule rebuild).
    useEffect(() => {
        const amt = hpAmt;
        const terms = form.terms || 0;
        const first = parseDDMMYYYY(form.firstRepayment);
        const totalInst = toNumber(form.totalInstalment);

        if (!amt || !terms || !first) {
            setScheduleRequest(null);
            return;
        }

        const tenorYears = terms / 12;
        const firstFormatted = formatYYYYMMDD(first);

        // If totalInstalment is provided, use it; otherwise require rate input
        if (totalInst > 0) {
            const systemMonthly = calcMonthlyInstalment(totalInst, terms);
            const effectiveMonthly = resolveMonthlyInstalmentForSave(systemMonthly, monthlyInstalmentInput);
            const req: LoanScheduleRequest = {
                loanAmount: amt,
                tenorYears,
                firstRepaymentDate: firstFormatted,
                totalInstalment: totalInst,
            };
            // Only send override when ≠ system ceil — backend uses it as fixed PMT for whole plan.
            if (toNumber(effectiveMonthly) !== toNumber(systemMonthly) && toNumber(effectiveMonthly) > 0) {
                req.monthlyInstalment = toNumber(effectiveMonthly);
            }
            setScheduleRequest(req);
        } else {
            setScheduleRequest(null);
        }
    }, [hpAmt, form.terms, form.firstRepayment, form.totalInstalment, monthlyInstalmentInput]);

    // Auto-open drawer when schedule request is ready
    useEffect(() => {
        if (!calcOpen || !scheduleRequest) return;
        const timer = setTimeout(() => {
            setDrawerOpen(true);
            onDrawerStateChange?.(true);
        }, 300);
        return () => clearTimeout(timer);
    }, [calcOpen, scheduleRequest]);

    // Check if loan is in the future (firstRepaymentDate > periodTo)
    const isFutureLoan = useMemo(() => {
        if (!form.firstRepayment || !periodTo) return false;
        try {
            const firstRepaymentDate = parseDDMMYYYY(form.firstRepayment);
            const periodToDate = parseDDMMYYYY(periodTo);
            if (firstRepaymentDate && periodToDate) {
                return firstRepaymentDate > periodToDate;
            }
        } catch (e) {
            console.warn('Failed to parse dates for future loan check:', e);
        }
        return false;
    }, [form.firstRepayment, periodTo]);

    /** Nested under WP Dialog: blur + skip restore so closing does not leave focus under aria-hidden. */
    const blurActiveElement = () => {
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
    };

    /** Prevent action buttons from keeping focus while the nested Modal applies aria-hidden. */
    const preventButtonFocus = (e: React.MouseEvent) => {
        e.preventDefault();
    };

    const handleClose = () => {
        blurActiveElement();
        setDrawerOpen(false);
        onDrawerStateChange?.(false);
        setCalcOpen(false);
        monthlyModeDecidedRef.current = false;
        setMonthlyInstalmentSplitActive(false);
    };

    const handleConfirm = () => {
        const isDefined = (v: unknown) => v !== undefined && v !== null;
        // HP Interest-only override: allow Confirm without required loan inputs
        // (purchaseCost/mv may be parent-injected; ignore them for this gate)
        const hasInterestChange =
            isDefined(sums.interestYoaChange)
            || (!hideLastYoa && isDefined(sums.interestLastYoaChange));
        const hasOtherSummaryChange =
            isDefined(sums.currentYoaChange)
            || isDefined(sums.currentLastYoaChange)
            || isDefined(sums.nonCurrentYoaChange)
            || isDefined(sums.balanceLastYoaChange);
        // WHY: formatNumber(0) → "0.00" 不能当作用户已填贷款表单
        const hasFormInput =
            toNumber(form.downPayment) > 0
            || !!(form.terms && form.terms > 0)
            || toNumber(form.totalInstalment) > 0
            || !!String(form.firstRepayment || '').trim();
        const skipRequiredValidation = hasInterestChange && !hasOtherSummaryChange && !hasFormInput;

        if (!skipRequiredValidation) {
            // Validate required fields: Purchase cost, Down payment, terms, total instalment, first repayment date
            const newErrors: Record<string, string> = {};
            if (!form.purchaseCost) newErrors['purchaseCost'] = 'Purchase cost is required';
            if (!form.downPayment) newErrors['downPayment'] = 'Down payment is required';
            if (!form.terms || form.terms <= 0) newErrors['terms'] = 'Terms is required';
            if (!form.totalInstalment) newErrors['totalInstalment'] = 'Total instalment is required';
            if (!form.firstRepayment) newErrors['firstRepayment'] = 'First repayment date is required';

            setFormError(newErrors);
            if (Object.keys(newErrors).length > 0) return;
        } else {
            setFormError({});
        }

        // Blur + close before onConfirm: parent may set controlled `open` false immediately.
        blurActiveElement();
        setDrawerOpen(false);
        onDrawerStateChange?.(false);
        setCalcOpen(false);
        monthlyModeDecidedRef.current = false;
        setMonthlyInstalmentSplitActive(false);
        try {
            onConfirm?.();
        } catch {
        }
    };

    // Helper: format name-value row
    const nameValue = (name: string, valueNode: React.ReactNode) => (
        <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1}}>
            <Typography variant="body2" sx={{minWidth: 140, pt: 0.5}}>{name}</Typography>
            <Box sx={{flex: 1}}>{valueNode}</Box>
        </Box>
    );

    // Compute horizontal shift so the Dialog clears the RepaymentScheduleDrawer (left-anchored)
    // and stays within the viewport regardless of browser size.
    useLayoutEffect(() => {
        const calcShift = () => {
            if (!cardRef.current) return 0;
            // Prefer the Dialog Paper width (more accurate than the inner Card)
            const paper = cardRef.current.closest('.MuiDialog-paper') as HTMLElement | null;
            const dialogWidth = (paper ? paper.getBoundingClientRect().width : cardRef.current.getBoundingClientRect().width);
            const viewportWidth = window.innerWidth;
            const viewportCenter = viewportWidth / 2;
            const margin = 16; // safety gap between drawer and dialog
            // Dialog is centered; its default left edge sits at viewportCenter - dialogWidth/2.
            // We need: (viewportCenter - dialogWidth/2) + shiftX >= drawerWidth + margin
            const required = drawerWidth + margin - (viewportCenter - dialogWidth / 2);
            // Don't let the dialog spill past the right viewport edge
            const maxShift = Math.max(0, viewportCenter - dialogWidth / 2 - margin);
            if (required <= 0) return 0;
            return Math.round(Math.min(required, maxShift));
        };

        const apply = () => {
            if (drawerOpen) {
                setShiftX(calcShift());
            } else {
                setShiftX(0);
            }
        };

        apply();
        // Recompute on resize while drawer is open
        const onResize = () => {
            if (drawerOpen) setShiftX(calcShift());
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [drawerOpen, drawerWidth]);

    // Ensure drawer closes (no leftover open state) if component unmounts
    useEffect(() => {
        return () => setDrawerOpen(false);
    }, []);

    // Close drawer when dialog closes and notify parent
    useEffect(() => {
        if (!calcOpen && drawerOpen) {
            setDrawerOpen(false);
            onDrawerStateChange?.(false);
        }
    }, [calcOpen]);

    // Notify parent on drawer state change
    useEffect(() => {
        onDrawerStateChange?.(drawerOpen);
    }, [drawerOpen]);

    // Empty → null (not 0): 0 is a valid override
    const handleSumsChange = (field: keyof VehicleLoanRow['sums'], value: string) => {
        const trimmed = String(value ?? '').trim();
        const numValue = trimmed === '' ? null : toNumber(trimmed);
        setSums({
            ...sums,
            [field]: numValue,
        });
    };

    // Handle cancel edit - clear manual override; persist null for save/API
    const handleSumsCancel = (field: keyof VehicleLoanRow['sums']) => {
        setSums({
            ...sums,
            [field]: null,
        });
    };

    /** MoneyField.suffixValue rejects null - map cleared Change to undefined for display. */
    const changeSuffix = (v: number | null | undefined): number | undefined =>
        v == null ? undefined : v;

    /** Hover Summary calc (split left) -> trace matching schedule period/column. */
    const clearScheduleTrace = React.useCallback(() => {
        setSummaryTrace(null);
    }, []);

    const bindScheduleTrace = React.useCallback((trace: BankLoanScheduleSummaryTrace | null) => {
        return (hovering: boolean) => {
            if (!hovering) {
                setSummaryTrace(null);
                return;
            }
            if (!trace) return;
            setDrawerOpen(true);
            onDrawerStateChange?.(true);
            setSummaryTrace(trace);
        };
    }, [onDrawerStateChange]);

    /** Summary *Change editable unless full readOnly (align BankLoanDetail). */
    const summaryChangeEditable = !readOnly;
    const lastYoaTotalDisplay = resolveVehicleLoanYearEndBalanceLast(sums);

    const splitCell = (
        value: number | undefined,
        changeField: keyof VehicleLoanRow['sums'],
        hover?: (hovering: boolean) => void,
        futureDash = false,
    ): LoanSummaryCell => {
        if (futureDash && isFutureLoan) return {mode: 'dash'};
        return {
            mode: 'split',
            value,
            suffixValue: changeSuffix(sums[changeField] as number | null | undefined),
            suffixEditable: summaryChangeEditable,
            onSuffixChange: summaryChangeEditable
                ? (n) => handleSumsChange(changeField, n == null ? '' : String(n))
                : undefined,
            onEditCancel: summaryChangeEditable ? () => handleSumsCancel(changeField) : undefined,
            onCalcHoverChange: hover,
        };
    };

    const SummaryCard = (
        <LoanSummaryCard
            yoa={yoa}
            lastYoa={lastYoa}
            hideLastYoa={hideLastYoa}
            onMouseLeave={clearScheduleTrace}
            interestYoa={splitCell(sums.interestYoa, 'interestYoaChange', bindScheduleTrace({period: 'curr', column: 'interest', mode: 'sum'}), true)}
            interestLastYoa={splitCell(sums.interestLastYoa, 'interestLastYoaChange', bindScheduleTrace({period: 'last', column: 'interest', mode: 'sum'}))}
            currentYoa={splitCell(sums.currentYoa, 'currentYoaChange', bindScheduleTrace({period: 'next', column: 'principal', mode: 'sum'}), true)}
            currentLastYoa={splitCell(sums.currentLastYoa, 'currentLastYoaChange', bindScheduleTrace({period: 'curr', column: 'principal', mode: 'sum'}))}
            nonCurrentYoa={splitCell(sums.nonCurrentYoa, 'nonCurrentYoaChange', bindScheduleTrace({period: 'next', column: 'balance', mode: 'last'}), true)}
            nonCurrentLastYoa={{
                mode: 'plainNumber',
                value: resolveNonCurrentLastYoa(sums, lastYoaTotalDisplay),
            }}
            balanceYoa={isFutureLoan
                ? {mode: 'dash'}
                : {mode: 'plainNumber', value: resolveBalanceYoa(sums)}}
            balanceLastYoa={splitCell(
                sums.balanceLastYoa ?? 0,
                'balanceLastYoaChange',
                bindScheduleTrace({period: 'last', column: 'balance', mode: 'last'}),
            )}
        />
    );

    return (
        <>
            {/* Single shared overlay mask rendered via Portal; sits below Dialog and Drawer */}
            <OverlayMask open={calcOpen}
                         onClick={() => {
                             // Do nothing on blank area click; closing is controlled via explicit actions
                         }}
            />
            {/* Trigger to open calculator dialog */}
            {!readOnly && (triggerVariant === 'icon' ? (
                <IconButton size="small" color="primary" onClick={() => setCalcOpen(true)} aria-label="Edit vehicle loan">
                    <EditIcon fontSize="small"/>
                </IconButton>
            ) : (
                <Button component="button" type="button" variant="text" onClick={() => setCalcOpen(true)} size="small">
                    Calculator
                </Button>
            ))}

            <Dialog
                sx={{
                    zIndex: (theme) => theme.zIndex.modal,
                    transition: 'transform 300ms ease',
                    transform: `translateX(${shiftX}px)`
                }}
                open={calcOpen}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
                disableEnforceFocus
                disableRestoreFocus
                closeAfterTransition={false}
                hideBackdrop
                slotProps={{
                    paper: {
                        'data-no-maximize': 'true',
                    } as any,
                }}
            >
                <DialogTitle>Vehicle Loan Calculator</DialogTitle>
                <DialogContent dividers>
                    <div ref={containerRef}>
                        <Card
                            ref={cardRef}
                            variant="outlined"
                        >
                            <CardContent>
                                {/* Summary always shown; values fill in after schedule calc */}
                                {SummaryCard}
                                <Divider sx={{mb: 2}}/>

                                <Stack spacing={1.25}>
                                    {nameValue(
                                        'MV',
                                        <Typography variant="body2" align="right">{form.mv || '-'}</Typography>
                                    )}
                                    {nameValue(
                                        'HP comp',
                                        <Box>
                                            <InputField
                                                value={form.hpComp}
                                                onChange={(e: any) => handleChange('hpComp', e.target.value)}
                                                size="small"
                                                align="right"
                                                error={!!formError.hpComp}
                                                helperText={formError.hpComp}
                                            />
                                        </Box>
                                    )}
                                    {nameValue(
                                        'Agreement no',
                                        <Box>
                                            <InputField
                                                value={form.agreementNo}
                                                onChange={(e: any) => handleChange('agreementNo', e.target.value)}
                                                size="small"
                                                align="right"
                                                error={!!formError.agreementNo}
                                                helperText={formError.agreementNo}
                                            />
                                        </Box>
                                    )}
                                    {nameValue(
                                        'Purchase cost',
                                        <Typography variant="body2" align="right">{form.purchaseCost || '-'}</Typography>
                                    )}
                                    {nameValue(
                                        'Down payment',
                                        <Box>
                                            <MoneyField
                                                editable
                                                align="right"
                                                compact
                                                value={form.downPayment}
                                                onChangeDisplay={(display) => handleChange('downPayment', display)}
                                                currencySymbol={currencySymbol}
                                            />
                                            {!!formError.downPayment && (
                                                <Typography variant="caption"
                                                            color="error">{formError.downPayment}</Typography>
                                            )}
                                        </Box>
                                    )}
                                    {nameValue(
                                        'HP amt',
                                        <Typography variant="body2" align="right">{formatNumber(hpAmt)}</Typography>
                                    )}
                                    {nameValue(
                                        'Terms (months)',
                                        <Box>
                                            <AutocompleteField
                                                options={VEHICLE_LOAN_TERM_OPTIONS}
                                                value={form.terms != null ? String(form.terms) : null}
                                                onChange={(v) => {
                                                    if (v == null || String(v).trim() === '') {
                                                        handleChange('terms', null);
                                                        return;
                                                    }
                                                    const n = Number(String(v).trim());
                                                    handleChange('terms', Number.isFinite(n) && n > 0 ? n : null);
                                                }}
                                                freeSolo
                                                decimalPlaces={0}
                                                commitOnBlur
                                                forcePopupIcon
                                                align="right"
                                                size="small"
                                            />
                                            {!!formError.terms && (
                                                <Typography variant="caption" color="error">{formError.terms}</Typography>
                                            )}
                                        </Box>
                                    )}
                                    {nameValue(
                                        'Total Instalment',
                                        <Box>
                                            <MoneyField
                                                editable
                                                align="right"
                                                compact
                                                value={form.totalInstalment}
                                                onChangeDisplay={(display) => handleChange('totalInstalment', display)}
                                                currencySymbol={currencySymbol}
                                            />
                                            {!!formError.totalInstalment && (
                                                <Typography variant="caption"
                                                            color="error">{formError.totalInstalment}</Typography>
                                            )}
                                        </Box>
                                    )}
                                    {nameValue(
                                        'Total interest',
                                        <Typography variant="body2"
                                                    align="right">{formatNumber(totalInterest)}</Typography>
                                    )}
                                    {nameValue(
                                        'First repayment date',
                                        <Box>
                                            <DatePicker
                                                value={form.firstRepayment}
                                                onChange={(val) => handleChange('firstRepayment', val || '')}
                                                compact
                                            />
                                            {!!formError.firstRepayment && (
                                                <Typography variant="caption"
                                                            color="error">{formError.firstRepayment}</Typography>
                                            )}
                                        </Box>
                                    )}
                                    {nameValue(
                                        'Last repayment date',
                                        <Typography variant="body2"
                                                    align="right">{lastRepaymentDate || '-'}</Typography>
                                    )}
                                    {nameValue(
                                        'Monthly instalment',
                                        <MoneyField
                                            align="right"
                                            compact
                                            value={monthlyInstalmentSplitActive
                                                ? monthlyInstalment
                                                : (monthlyInstalmentInput || monthlyInstalment)}
                                            separator=">"
                                            suffixValue={monthlyInstalmentSplitActive ? monthlyInstalmentInput : 0}
                                            forceShowSplitMode={monthlyInstalmentSplitActive}
                                            icon={<EditIcon fontSize="small"/>}
                                            cancelIcon={<CloseIcon fontSize="small"/>}
                                            onEditStart={() => {
                                                setMonthlyInstalmentSplitActive(true);
                                                setMonthlyInstalmentInput((cur) => (
                                                    toNumber(cur) > 0 ? cur : monthlyInstalment
                                                ));
                                            }}
                                            onSuffixChange={(n) => {
                                                setMonthlyInstalmentInput(n);
                                                // Stale row overrides must not sit on top of a monthly-driven rebuild.
                                                setSums((prev) => (
                                                    prev.editedInstalments
                                                        ? {...prev, editedInstalments: null}
                                                        : prev
                                                ));
                                                // Allow re-decide idle/split after backend rebuild.
                                                monthlyModeDecidedRef.current = false;
                                            }}
                                            onEditCancel={() => {
                                                setMonthlyInstalmentSplitActive(false);
                                                setMonthlyInstalmentInput(monthlyInstalment);
                                                setSums((prev) => (
                                                    prev.editedInstalments
                                                        ? {...prev, editedInstalments: null}
                                                        : prev
                                                ));
                                                monthlyModeDecidedRef.current = false;
                                            }}
                                            currencySymbol={currencySymbol}
                                        />
                                    )}
                                </Stack>
                            </CardContent>
                            {/* Drawer mounted alongside to share same overlay; interactive concurrently */}
                            <RepaymentScheduleDrawer
                                open={drawerOpen}
                                onClose={() => {
                                    setDrawerOpen(false);
                                    onDrawerStateChange?.(false);
                                }}
                                loanType="vehicle"
                                annualId={annualId}
                                scheduleRequest={scheduleRequest}
                                onDataLoaded={(data) => {
                                    setScheduleData(data.scheduleData);
                                    if (!('interestYoa' in data.sums) && !('currentYoa' in data.sums)) return;
                                    // Drawer payload has no *Change; spread keeps keyboard overrides.
                                    setSums((prev) => ({...prev, ...data.sums}));
                                    // Decide idle vs split once from Drawer init (inCurr vs Monthly).
                                    if (!monthlyModeDecidedRef.current) {
                                        const schedule = data.scheduleData.schedule || [];
                                        const pf = data.periods.periodFrom;
                                        const pt = data.periods.periodTo;
                                        // Wait for period window so inCurr filter is meaningful.
                                        if (schedule.length > 0 && pf == null && pt == null) {
                                            /* next onDataLoaded */
                                        } else {
                                            monthlyModeDecidedRef.current = true;
                                            const monthlyValue = resolveMonthlyInstalmentForSave(
                                                monthlyInstalmentRef.current,
                                                monthlyInstalmentInputRef.current,
                                            );
                                            const match = inCurrInstalmentsMatchMonthly(
                                                schedule,
                                                pf,
                                                pt,
                                                monthlyValue,
                                            );
                                            setMonthlyInstalmentSplitActive(!match);
                                        }
                                    }
                                }}
                                initialOverrides={sums.prevFinalBalance != null || sums.currFinalBalance != null || sums.editedInstalments ? {
                                    prevFinalBalance: sums.prevFinalBalance,
                                    currFinalBalance: sums.currFinalBalance,
                                    editedInstalments: sums.editedInstalments,
                                } : null}
                                onWidthChange={setDrawerWidth}
                                summaryTrace={summaryTrace}
                            />
                        </Card>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onMouseDown={preventButtonFocus} onClick={handleClose}>Close</Button>
                    {!readOnly && (
                        <Button variant="contained" onMouseDown={preventButtonFocus} onClick={handleConfirm}>
                            Confirm
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
}

